import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { FeatureFlagIdGrantReasons } from 'c1gen/features'
import { AppAvatar } from 'components/core/avatar/AppAvatar'
import { C1Card } from 'components/core/card/C1Card'
import { C1Icon } from 'components/core/icons/C1Icon'
import {
    Status,
    Usage,
    Cost,
    Risk,
    Analysis,
    EmploymentStatus,
    ComplianceFramework,
    InsightItem,
} from 'components/core/insights'
import { useFeatureChecker } from 'components/no-ui/capability'
import { GrantOriginForInsights } from 'components/pages/entitlement/grant-origin/GrantOrigin'
import { TaskValue } from 'lib/actions/task'
import { Sparkles } from 'lucide-react'
import { GetAppRequest } from 'pbts/c1/api/app/v1/app'
import { ExternalSecurityInsight, Insight } from 'pbts/c1/api/task/v1/insights'
import { FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { appGetApi } from 'redux_toolkit/apps'

type TaskInsightsTabProps = {
    task: TaskValue
}

type InsightSectionProps = {
    title: React.ReactNode
    icon?: React.ReactNode
    children: React.ReactNode
}

const InsightSection: FC<InsightSectionProps> = ({ title, icon, children }) => {
    return (
        <C1Card sx={{ mb: 0, p: 0, overflow: 'hidden' }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                {icon}
                <Typography variant="h6" component="div">
                    {title}
                </Typography>
            </Stack>
            <Box sx={{ px: 3, py: 2 }}>
                {children}
            </Box>
        </C1Card>
    )
}

type ExternalSecuritySectionProps = {
    sourceAppId: string
    insights: Array<{ insight: Insight; externalSecurity: ExternalSecurityInsight }>
}

const ExternalSecuritySection: FC<ExternalSecuritySectionProps> = ({ sourceAppId, insights }) => {
    const { data: app, isLoading } = appGetApi.useGet(
        GetAppRequest.create({ id: sourceAppId }),
        { actionable: !!sourceAppId },
    )

    const title = isLoading ? (
        <Skeleton variant="text" width={150} />
    ) : (
        <FormattedMessage
            id="components_task_insights_tab_external_section_title"
            defaultMessage="{app_name} insights"
            values={{ app_name: app?.displayName || sourceAppId }}
        />
    )

    const icon = app ? (
        <AppAvatar appId={app.id} appName={app.displayName} size="sm" decorative />
    ) : null

    return (
        <InsightSection title={title} icon={icon}>
            <Stack spacing={1}>
                {insights.map(({ insight, externalSecurity }, i) => {
                    const label = getExternalSecurityLabel(externalSecurity)
                    return (
                        <InsightItem key={`external_${i}`} insightType={insight.type}>
                            <Typography component="span">{label}</Typography>
                        </InsightItem>
                    )
                })}
            </Stack>
        </InsightSection>
    )
}

const getExternalSecurityLabel = (externalSecurity: ExternalSecurityInsight): React.ReactNode => {
    switch (externalSecurity.insightType.oneofKind) {
        case 'riskScore':
            return (
                <FormattedMessage
                    id="components_task_insights_tab_external_risk_score"
                    defaultMessage="Risk score: <b>{value}</b>"
                    values={{
                        b: (chunks): React.ReactElement => <b>{chunks}</b>,
                        value: externalSecurity.insightType.riskScore.value,
                    }}
                />
            )
        case 'issue':
            return (
                <FormattedMessage
                    id="components_task_insights_tab_external_issue"
                    defaultMessage="Security issue: <b>{value}</b>{severity}"
                    values={{
                        b: (chunks): React.ReactElement => <b>{chunks}</b>,
                        value: externalSecurity.insightType.issue.value,
                        severity: externalSecurity.insightType.issue.severity
                            ? ` (${externalSecurity.insightType.issue.severity})`
                            : '',
                    }}
                />
            )
        default:
            return (
                <FormattedMessage
                    id="components_task_insights_tab_external_unknown"
                    defaultMessage="External security insight"
                />
            )
    }
}

type GroupedInsights = {
    aiInsights: Insight[]
    externalInsightsBySource: Map<string, Array<{ insight: Insight; externalSecurity: ExternalSecurityInsight }>>
}

const groupInsights = (insights: Insight[] | undefined, showGrantOrigins: boolean): GroupedInsights => {
    const aiInsights: Insight[] = []
    const externalInsightsBySource = new Map<string, Array<{ insight: Insight; externalSecurity: ExternalSecurityInsight }>>()

    if (!insights) {
        return { aiInsights, externalInsightsBySource }
    }

    const sortedInsights = [...insights].sort((a, b) => b.type - a.type)

    for (const insight of sortedInsights) {
        if (insight.kind.oneofKind === 'externalSecurity') {
            const sourceAppId = insight.kind.externalSecurity.sourceAppId
            if (!externalInsightsBySource.has(sourceAppId)) {
                externalInsightsBySource.set(sourceAppId, [])
            }
            const sourceInsights = externalInsightsBySource.get(sourceAppId)
            if (sourceInsights) {
                sourceInsights.push({
                    insight,
                    externalSecurity: insight.kind.externalSecurity,
                })
            }
        } else if (insight.kind.oneofKind === 'grantOrigin') {
            // Skip grant origins as per requirements
            if (showGrantOrigins) {
                aiInsights.push(insight)
            }
        } else if (insight.kind.oneofKind !== undefined) {
            aiInsights.push(insight)
        }
    }

    return { aiInsights, externalInsightsBySource }
}

const renderAIInsight = (insight: Insight, index: number, task: TaskValue, showGrantOrigins: boolean): React.ReactElement | null => {
    switch (insight.kind.oneofKind) {
        case 'userStatus':
            return (
                <Status
                    key={`status_${index}`}
                    userStatus={insight.kind.userStatus.userStatus}
                    user={task.identityUser || task.user}
                    insightType={insight.type}
                />
            )
        case 'employmentStatus':
            return (
                <EmploymentStatus
                    key={`employment_${index}`}
                    employmentStatus={insight.kind.employmentStatus.employmentStatus}
                    employmentStatusSources={insight.kind.employmentStatus.employmentStatusSources}
                    user={task.identityUser || task.user}
                    insightType={insight.type}
                />
            )
        case 'usage':
            return (
                <Usage
                    key={`usage_${index}`}
                    usage={insight.kind.usage}
                    app={task.app}
                    appUser={task.appUser}
                    insightType={insight.type}
                />
            )
        case 'risk':
            return (
                <Risk
                    key={`risk_${index}`}
                    risk={insight.kind.risk}
                    insightType={insight.type}
                    task={task}
                />
            )
        case 'complianceFramework':
            return (
                <ComplianceFramework
                    key={`compliance_${index}`}
                    complianceFramework={insight.kind.complianceFramework}
                    insightType={insight.type}
                />
            )
        case 'cost':
            return (
                <Cost
                    key={`cost_${index}`}
                    cost={insight.kind.cost}
                    insightType={insight.type}
                />
            )
        case 'analysis':
            return (
                <Analysis
                    key={`analysis_${index}`}
                    analysis={insight.kind.analysis}
                    insightType={insight.type}
                    task={task}
                />
            )
        case 'grantOrigin':
            if (showGrantOrigins) {
                return (
                    <GrantOriginForInsights
                        key={`grantOrigin_${index}`}
                        grantOrigin={insight.kind.grantOrigin}
                        insightType={insight.type}
                    />
                )
            }
            return null
        default:
            return null
    }
}

export const TaskInsightsTab: FC<TaskInsightsTabProps> = ({ task }) => {
    const hasGrantReasonsFF = useFeatureChecker(FeatureFlagIdGrantReasons)
    const showGrantOrigins = task.type?.taskType.oneofKind === 'certify' && !!hasGrantReasonsFF

    const { aiInsights, externalInsightsBySource } = useMemo(
        () => groupInsights(task.insights, showGrantOrigins),
        [task.insights, showGrantOrigins],
    )

    const hasAIInsights = aiInsights.length > 0
    const hasExternalInsights = externalInsightsBySource.size > 0

    if (!hasAIInsights && !hasExternalInsights) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    <FormattedMessage
                        id="components_task_insights_tab_no_insights"
                        defaultMessage="No insights available for this task"
                    />
                </Typography>
            </Box>
        )
    }

    return (
        <Stack spacing={3} pt={2}>
            {hasAIInsights && (
                <InsightSection
                    title={
                        <FormattedMessage
                            id="components_task_insights_tab_ai_section_title"
                            defaultMessage="AI insights"
                        />
                    }
                    icon={<C1Icon icon={Sparkles} sx={{ color: 'primary.main', fontSize: 20 }} />}
                >
                    <Stack spacing={1}>
                        {aiInsights.map((insight, index) => renderAIInsight(insight, index, task, showGrantOrigins))}
                    </Stack>
                </InsightSection>
            )}

            {Array.from(externalInsightsBySource.entries()).map(([sourceAppId, insights]) => (
                <ExternalSecuritySection
                    key={sourceAppId}
                    sourceAppId={sourceAppId}
                    insights={insights}
                />
            ))}
        </Stack>
    )
}
